import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {getTablesByDocumentNameFromDocumentIds, TableLimitUtils} from 'notebook/performance/tableLimitUtils';
import {Table} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note} from 'notebook/util/NoteUtilConstants';
import {GRAY_DARK, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface PerformanceMonitorToggleProps {
  tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>};
  openModal: () => void;
}

interface NotebookPerformanceMonitorToggleProps {
  note: Note;
  openModal: () => void;
}

interface MultiDocumentPerformanceMonitorToggleProps {
  documentIds: Array<string>;
  openModal: () => void;
}

export function MultiDocumentPerformanceMonitorToggle({
  documentIds,
  openModal,
}: MultiDocumentPerformanceMonitorToggleProps): JSX.Element {
  const tablesByDocumentName = getTablesByDocumentNameFromDocumentIds(documentIds);
  return <PerformanceMonitorToggle tablesByDocumentName={tablesByDocumentName} openModal={openModal} />;
}

export function NotebookPerformanceMonitorToggle({
  note,
  openModal,
}: NotebookPerformanceMonitorToggleProps): JSX.Element {
  const tablesByDocumentName = useMemo(() => {
    const tables = TableUtil.getTablesFromNote(note);
    return {entry: tables};
  }, [note]);
  return (
    <BufferOptionsItem className="u-vertical-center-items">
      <PerformanceMonitorToggle tablesByDocumentName={tablesByDocumentName} openModal={openModal} />
    </BufferOptionsItem>
  );
}

function PerformanceMonitorToggle({
  tablesByDocumentName,
  openModal,
}: PerformanceMonitorToggleProps): JSX.Element {
  const {numCells} = useMemo(
    () => TableLimitUtils.analyzePerformanceOfTables(tablesByDocumentName),
    [tablesByDocumentName]
  );
  let icon: BenchlingIconName = 'gauge-min';
  let iconColor = styles.defaultColor;
  if (TableLimitUtils.sizeIsOverMaximum(numCells)) {
    icon = 'gauge-max';
    iconColor = styles.errorColor;
  } else if (TableLimitUtils.sizeIsInWarningRange(numCells)) {
    icon = 'gauge';
    iconColor = styles.warningColor;
  }

  return (
    <BenchlingIcon
      icon={icon}
      className={css(iconColor, styles.icon)}
      altText="Table Performance Monitor"
      onClick={openModal}
    />
  );
}

const styles = StyleSheet.create({
  warningColor: {
    color: ORANGE,
  },
  errorColor: {
    color: RED,
  },
  defaultColor: {
    color: GRAY_DARK,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    height: '16px',
    width: '16px',
    ':hover': {
      cursor: 'pointer',
    },
  },
});
